import React from 'react'
import { Divider, makeStyles, tokens } from '@fluentui/react-components'
import { t } from 'i18next'
import { RiskStatus, SimplifiedRisk } from '../../../api/schemas/schema'
import { GridItem } from '../../common/grid/GridItem'
import { ControlledRadioGroup } from '../../common/inputs/ControlledRadioGroup'
import { Control, UseFormWatch } from 'react-hook-form'
import { ControlledMoneyInput } from '../../common/inputs/ControlledMoneyInput'
import { ControlledTextInput } from '../../common/inputs/TextInput'
import { ProtectedAssetGoalPopover } from '../../common/popover/ProtectedAssetGoalPopover'
import { ClipboardTextLtr20Filled, ClipboardTextLtr20Regular } from '@fluentui/react-icons'
import { InformationPopover } from '../../common/popover/InformationPopover'
import { SimplifiedRiskCriteria } from './SimplifiedRiskAnalysis/SimplifiedRiskCriteria'
import TextOutput from '../../common/inputs/TextOutput'

const useStyles = makeStyles({
   column: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalXS,
   },
   popoverWrapper: {
      display: 'flex',
      justifyContent: 'space-around',
      paddingBottom: tokens.spacingVerticalM,
   },
   hideInPhone: {
      '@media screen and (max-width: 1007px)': {
         display: 'none',
      },
   },
   showInPhone: {
      display: 'none',
      '@media screen and (max-width: 1007px)': {
         display: 'block',
      },
   },
})

type RiskActionPlanInfoProps = {
   control: Control<SimplifiedRisk, any>
   watch: UseFormWatch<SimplifiedRisk>
   risk: SimplifiedRisk
}
export const RiskActionPlanInfo = ({ control, watch, risk }: RiskActionPlanInfoProps) => {
   const classes = useStyles()

   return (
      <>
         <GridItem size="12/12" className={classes.hideInPhone}>
            <>
               <div className={classes.popoverWrapper}>
                  {risk?.protectedAssetGoal && <ProtectedAssetGoalPopover protectedAssetGoal={risk?.protectedAssetGoal} />}
                  <InformationPopover
                     btnLabel={t('RiskCriteria')}
                     popoverLabel={t('RiskCriteria')}
                     input={<SimplifiedRiskCriteria />}
                     regularIcon={<ClipboardTextLtr20Regular />}
                     filledIcon={<ClipboardTextLtr20Filled />}
                  />
               </div>
               <Divider appearance="brand" />
            </>
         </GridItem>
         <GridItem size="12/12" className={classes.showInPhone}>
            <Divider appearance="brand" />
         </GridItem>
         <GridItem size="12/12">
            <TextOutput label={t('RiskScenario')} text={risk?.scenario} />
         </GridItem>
         <GridItem size="12/12">
            <TextOutput label={t('RiskSource')} text={risk?.source} />
         </GridItem>
         <GridItem size="12/12">
            <TextOutput label={t('WeaknessesOrCurrentProtection')} text={risk?.currentState} />
         </GridItem>
         <GridItem size="12/12">
            <TextOutput label={t('Consequence')} text={risk?.consequence} />
         </GridItem>
         <GridItem size="12/12">
            <TextOutput label={t('UnderlyingMotivationAndStand')} text={risk?.underlyingMotivation} />
         </GridItem>
         <GridItem size="12/12">
            <Divider appearance="brand" />
         </GridItem>
         <GridItem size="12/12">
            <ControlledRadioGroup
               control={control}
               name={'status'}
               label={t('RiskLevel')}
               required
               options={[
                  { label: t('Unacceptable'), value: RiskStatus.Unacceptable },
                  { label: t('Questionable'), value: RiskStatus.Questionable },
                  { label: t('Acceptable'), value: RiskStatus.Acceptable },
               ]}
               convertValue
            />
         </GridItem>
         {(watch('status') === RiskStatus.Unacceptable || watch('status') === RiskStatus.Questionable) && (
            <>
               <GridItem size="12/12">
                  <ControlledTextInput control={control} name={'proposedAction'} label={t('ProposedAction')} multiline required />
               </GridItem>
               <GridItem size="12/12">
                  <ControlledMoneyInput control={control} name={'estimatedCost'} label={t('EstimatedCost')} />
               </GridItem>
            </>
         )}
         <GridItem size="12/12">
            <ControlledTextInput control={control} name={'comment'} label={t('UnderlyingMotivationAndStand')} multiline />
         </GridItem>
      </>
   )
}
